/**
 * StatusBadge - Unified status badge component
 *
 * Uses workflow configuration colors by default, with neutral fallback styles.
 * This is the single source of truth for all status display in the application.
 */

import { cn } from '../../lib/utils';
import { useWorkflowByType, getStatusColors, getStatusLabel as getWorkflowStatusLabel } from '../../hooks/useWorkflowConfig';
import { badgeStyles } from '../../lib/styles';
import type { UnifiedBookingStatus, UnifiedCaseType, CompleteWorkflow } from '@leal-crm/shared';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'orange';
type BadgeSize = 'xs' | 'sm' | 'md';

interface StatusBadgeProps {
  /** Status code (will be looked up for color and label) */
  status?: string;
  /** Custom label (overrides status/workflow lookup) */
  label?: string;
  /** Badge variant (overrides status lookup - for non-booking statuses) */
  variant?: BadgeVariant;
  /** Booking type to get workflow colors from (enables workflow-based colors) */
  bookingType?: UnifiedCaseType;
  /** Pre-loaded workflow (optional, for performance when rendering many badges) */
  workflow?: CompleteWorkflow | null;
  /** Size variant */
  size?: BadgeSize;
  /** Show colored dot indicator */
  showDot?: boolean;
  /** Additional className */
  className?: string;
}

const sizeClasses: Record<BadgeSize, string> = {
  xs: 'px-1.5 py-0.5 text-[10px]',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

const dotSizeClasses: Record<BadgeSize, string> = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
};

/**
 * Unified status badge component
 *
 * Usage:
 * - For booking/job statuses: <StatusBadge status="CONFIRMED" bookingType="private" />
 * - For simple variant badges: <StatusBadge variant="success" label="Active" />
 * - For non-workflow usage: <StatusBadge status="CONFIRMED" /> (neutral fallback)
 */
export function StatusBadge({
  status,
  label,
  variant,
  bookingType,
  workflow: providedWorkflow,
  size = 'sm',
  showDot = false,
  className,
}: StatusBadgeProps) {
  // Only fetch workflow if bookingType is provided and workflow not already given
  const shouldFetchWorkflow = bookingType && !providedWorkflow;
  const { data: fetchedWorkflow } = useWorkflowByType(shouldFetchWorkflow ? bookingType : undefined);
  const workflow = providedWorkflow ?? fetchedWorkflow;

  // Case 1: Direct variant usage (for non-status badges)
  if (variant && !status) {
    const variantClass = badgeStyles[variant];
    return (
      <span className={cn(badgeStyles.base, variantClass, sizeClasses[size], className)}>
        {showDot && <span className={cn('rounded-full bg-current opacity-70', dotSizeClasses[size])} />}
        {label || 'Unknown'}
      </span>
    );
  }

  // Case 2: Status with workflow colors (preferred)
  if (status && workflow) {
    const colors = getStatusColors(workflow, status as UnifiedBookingStatus);
    const displayLabel = label || getWorkflowStatusLabel(workflow, status as UnifiedBookingStatus, getFallbackLabel(status));

    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 font-medium rounded-md border',
          sizeClasses[size],
          className
        )}
        style={{
          color: colors.text,
          backgroundColor: colors.bg,
          borderColor: colors.border,
        }}
      >
        {showDot && (
          <span
            className={cn('rounded-full', dotSizeClasses[size])}
            style={{ backgroundColor: colors.text }}
          />
        )}
        {displayLabel}
      </span>
    );
  }

  // Case 3: Status without workflow (neutral fallback)
  if (status) {
    const displayLabel = label || getFallbackLabel(status);
    return (
      <span className={cn(badgeStyles.base, badgeStyles.default, sizeClasses[size], className)}>
        {showDot && <span className={cn('rounded-full bg-current opacity-70', dotSizeClasses[size])} />}
        {displayLabel}
      </span>
    );
  }

  // Case 4: Fallback (no status, no variant)
  return (
    <span className={cn(badgeStyles.base, badgeStyles.default, sizeClasses[size], className)}>
      {label || 'Unknown'}
    </span>
  );
}

/**
 * Status dot indicator only (no text)
 * Useful for compact displays like tables
 */
export function StatusDot({
  status,
  bookingType,
  workflow: providedWorkflow,
  size = 'sm',
  className,
}: Omit<StatusBadgeProps, 'label' | 'showDot' | 'variant'>) {
  const shouldFetchWorkflow = bookingType && !providedWorkflow;
  const { data: fetchedWorkflow } = useWorkflowByType(shouldFetchWorkflow ? bookingType : undefined);
  const workflow = providedWorkflow ?? fetchedWorkflow;

  let bgColor = '#9CA3AF';
  let displayLabel = status || 'Unknown';

  if (status && workflow) {
    const colors = getStatusColors(workflow, status as UnifiedBookingStatus);
    bgColor = colors.text;
    displayLabel = getWorkflowStatusLabel(workflow, status as UnifiedBookingStatus, getFallbackLabel(status));
  }

  return (
    <span
      className={cn('rounded-full', dotSizeClasses[size], className)}
      style={{ backgroundColor: bgColor }}
      title={displayLabel}
    />
  );
}

/**
 * Status tag with full color background (more prominent)
 * Useful for headers and key information
 */
export function StatusTag({
  status,
  bookingType,
  workflow: providedWorkflow,
  label,
  className,
}: Omit<StatusBadgeProps, 'size' | 'showDot' | 'variant'>) {
  const shouldFetchWorkflow = bookingType && !providedWorkflow;
  const { data: fetchedWorkflow } = useWorkflowByType(shouldFetchWorkflow ? bookingType : undefined);
  const workflow = providedWorkflow ?? fetchedWorkflow;

  if (status && workflow) {
    const colors = getStatusColors(workflow, status as UnifiedBookingStatus);
    const displayLabel = label || getWorkflowStatusLabel(workflow, status as UnifiedBookingStatus, getFallbackLabel(status));

    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border',
          className
        )}
        style={{
          color: colors.text,
          backgroundColor: colors.bg,
          borderColor: colors.border,
        }}
      >
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: colors.text }}
        />
        {displayLabel}
      </span>
    );
  }

  const displayLabel = label || getFallbackLabel(status || '');

  return (
    <span className={cn(badgeStyles.base, badgeStyles.default, 'px-3 py-1.5 text-sm font-semibold rounded-lg', className)}>
      {displayLabel}
    </span>
  );
}

/**
 * Get a human-readable fallback label from a status code
 */
function getFallbackLabel(status: string): string {
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Re-export for backwards compatibility
export { WorkflowStatusBadge, WorkflowStatusDot, WorkflowStatusTag } from './WorkflowStatusBadge';
